import React, { Component, createRef } from "react";
import { StyleSheet, View, TextInput, Text, TouchableOpacity, Platform, InputAccessoryView, Alert } from "react-native";
import { isMobileWeb, isMobileWebDevice, getNoAutofillProps, noAutofillRef, scannerFieldRef } from "../../utils/mobileWeb";
import { connect } from "react-redux";
import { searchItems } from "../../actions/records";
import { getMyCustomers, getMySuppliers } from "../../actions/submissions";
import DropDownLocations from "../widgets/dropdownLocations";
import colors from "../../constants/colors";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import Swal from "sweetalert2";
import BarcodeScannerComponent from "../widgets/BarCodeScannerComponent";
import KeyboardBar from "../widgets/KeyboardBar";
import { inputFocus } from "../../utils";
import {
  findLocationInList,
  locationListIncludesTerm,
  parseScannedValue,
  renderLocationLabel,
  getTextInputDomNode,
  cleanScannedText,
  normalizeWedgeScanText,
  isIncompleteWedgePrefix,
  isGarbledWedgeScanText,
  stopSymbolWedgeSessionForOwner,
  extendSymbolScanEnterShield,
  primeWedgeEnterShield,
  startSymbolWedgeSession,
  ensureSymbolWedgeCaptureReady,
  clearPassiveWedgeCaptureValue,
  registerActiveLocationWedgeField,
  unregisterActiveLocationWedgeField,
  getActiveLocationWedgeOwnerId,
  dedupeDoubledWedgeScan,
} from "../../utils/locationScan";
import { KeyboardAvoidingView } from "react-native-web";
import InventoryTransferFromLocationModal from "../InventoryTransferFromLocationModal";
import { unwrapLocationValue } from "../../utils/inventoryTransferLocationTree";
import { resolveTransferLocation, getTransferLocationPathLabel } from "../InventoryTransferLotsPanel";

const BLUR_RESOLVE_MS = 250;
const WEDGE_SETTLE_STABLE_MS = 200;
const WEDGE_SETTLE_MAX_MS = 2500;
const WEDGE_SETTLE_POLL_MS = 50;
const WEDGE_ENTER_READ_DELAYS_MS = [100, 80, 80, 100, 120, 150, 200, 250];
const WEDGE_MANUAL_KB_EVENT = "gohaccp-wedge-manual-keyboard";
/** Pause before resolving a manually typed location (keyboard). */
const MANUAL_TYPE_DEBOUNCE_MS = 1500;
/** Pause before search/filter on non-scanner location fields. */
const TYPE_SEARCH_DEBOUNCE_MS = 600;
/** Fast resolve for hardware wedge / scanner keyboard only. */
const SCAN_RESOLVE_DELAY_MS = 120;

class SearchInventoryLocations extends Component {
    inputRef = createRef();
    _mounted = false;
    _handlingScan = false;

    constructor(props) {
        super(props);
        this._interval = null;
        this._resolveTimer = null;
        const useScanner = (
            (props.isShipmentByLocation && props.defaultLabel === "location") ||
            (props.isTransferByLocation && (props.defaultLabel === "fromLocation" || props.defaultLabel === "toLocation")) ||
            (props.isInventoryTransfer && !props.isTransferByLocation && (props.defaultLabel === "fromLocation" || props.defaultLabel === "toLocation")) ||
            (props.isReceivingLog && props.defaultLabel === "toLocation") ||
            (props.showLocationsSearch && (props.defaultLabel === "location" || props.defaultLabel === "fromLocation" || props.defaultLabel === "toLocation"))
        );
        const laserFirstField =
            (props.isShipmentByLocation && props.defaultLabel === "location") ||
            (props.isTransferByLocation && (props.defaultLabel === "fromLocation" || props.defaultLabel === "toLocation"));
        const receivingToLocation = props.isReceivingLog && props.defaultLabel === "toLocation";
        const receivingStyleSearch =
            receivingToLocation ||
            !!(props.isRecipe && props.defaultLabel === "toLocation");
        this.state = {
            value: (props.defaultLabel == "toLocation" || props.showLocationsSearch) ? renderLocationLabel(props.initial) : typeof props.initial == "string" ? props.initial : "",
            locationSelected: props.initial && typeof props.initial == "object" ? props.initial : [],
            height: 30,
            items: props.options ? props.options : [],
            filtered: props.options ? props.options : [],
            wasEdited: false,
            openList: false,
            scan: false,
            scanerCanceled: false,
            wedgeInputVersion: 0,
            // Receiving-style location search always uses soft keyboard; laser-first only for shipment/transfer scan rows.
            softFocus: receivingStyleSearch ? true : (laserFirstField ? false : (useScanner && props.scannerInput ? false : true)),
            inputAccessoryViewID: useScanner ? `location-${props.id}-${props.position}` : undefined,
            showFromLocationModal: false,
        };
    }
    isReceivingToLocation(props = this.props) {
        return !!(props.isReceivingLog && props.defaultLabel === "toLocation");
    }

    /** Recipe Yield To Location: same type-to-search UX as receiving toLocation. */
    isYieldLocationSearch(props = this.props) {
        return !!(props.isRecipe && props.defaultLabel === "toLocation");
    }

    /** Soft-keyboard searchable location fields (receiving toLocation + recipe yield toLocation). */
    isReceivingStyleLocationSearch(props = this.props) {
        return this.isReceivingToLocation(props) || this.isYieldLocationSearch(props);
    }

    isLocationByFormScanField() {
        const { isShipmentByLocation, isTransferByLocation, isReceivingLog, defaultLabel } = this.props;
        if (isTransferByLocation && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) return true;
        if (isShipmentByLocation && defaultLabel === "location") return true;
        // Receiving destination: camera allowed, but not shipment/wedge laser-first UX.
        if (isReceivingLog && defaultLabel === "toLocation") return true;
        return false;
    }

    isSourceLocationField() {
        const { isShipmentByLocation, isTransferByLocation, showLocationsSearch, defaultLabel } = this.props;
        if (isShipmentByLocation && showLocationsSearch && defaultLabel === "location") return true;
        if (isTransferByLocation && defaultLabel === "fromLocation") return true;
        return false;
    }

    /** Shipment-style scanner resolve for location fields (wedge, camera, blur). */
    usesShipmentStyleLocationScan() {
        // Receiving-style searchable destination: type-to-search + dropdown (camera/wedge still via scan field helpers).
        if (this.isReceivingStyleLocationSearch()) return false;
        if (this.isLocationByFormScanField()) return true;
        // Classic inventory-transfer search fields stay list-based.
        if (this.props.isInventoryTransfer && !this.props.isTransferByLocation) return false;
        const { isShipmentByLocation, showLocationsSearch, defaultLabel } = this.props;
        if (!showLocationsSearch) return false;
        if (isShipmentByLocation && defaultLabel === "location") return true;
        if (defaultLabel === "toLocation" || defaultLabel === "fromLocation") return true;
        return false;
    }

    isLocationByForm() {
        return !!(
            this.props.isShipmentByLocation ||
            this.props.isTransferByLocation ||
            (this.props.isReceivingLog && this.props.defaultLabel === "toLocation")
        );
    }

    componentDidMount() {
        this._mounted = true;
        if (this.usesWebHardwareScanner() && this.isLocationByFormScanField()) {
            primeWedgeEnterShield(15000);
            ensureSymbolWedgeCaptureReady();
        }
        if (this.usesWebHardwareScanner()) {
            setTimeout(() => this.attachDomScanListeners(), 0);
        }
        if (this.usesWebHardwareScanner() && typeof window !== "undefined") {
            this._onWedgeManualKeyboard = (e) => {
                const { id, position } = e?.detail || {};
                if (id === this.props.id && position === this.props.position) {
                    this.enableManualKeyboard();
                }
            };
            window.addEventListener(WEDGE_MANUAL_KB_EVENT, this._onWedgeManualKeyboard);
        }
    }

    isEmptyInitial(initial) {
        if (initial == null || initial === "") return true;
        if (Array.isArray(initial) && initial.length === 0) return true;
        return false;
    }

    getInitialDisplayText(initial) {
        if (this.isEmptyInitial(initial)) return "";
        if (typeof initial === "string") return initial;
        return renderLocationLabel(initial);
    }

    locationInitialEquals(a, b) {
        return this.getInitialDisplayText(a) === this.getInitialDisplayText(b);
    }

    syncFromSubmissionInitial(initial, { force = false } = {}) {
        const display = this.getInitialDisplayText(initial);
        // While the user is typing/deleting, do not overwrite the field from form state.
        if (!force && this.state.wasEdited) {
            return;
        }
        if (!force && this.state.value === display && !this.state.wasEdited) {
            return;
        }
        if (display === "") {
            if (!force) {
                if (this.state.value) return;
                if (!this.isEmptyInitial(initial)) return;
            }
            if (force || this.isEmptyInitial(initial)) {
                this.clearLocationField();
            }
            return;
        }
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        this._lastScanProcessed = "";
        this.setState({
            value: display,
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
            scanerCanceled: false,
        }, () => {
            this.syncDomScanValue(display);
        });
    }

    shouldAllowBlurResolve() {
        if (this._suppressBlurResolveUntil && Date.now() < this._suppressBlurResolveUntil) {
            return false;
        }
        if (this._manualKeyboardMode || this.state.softFocus) {
            const dom = getTextInputDomNode(this.inputRef);
            if (dom && typeof document !== "undefined" && document.activeElement === dom) {
                return false;
            }
        }
        if (this.isEmptyInitial(this.props.initial) && !this.state.wasEdited && !this.usesShipmentStyleLocationScan()) {
            return false;
        }
        return true;
    }

    isActiveWedgeField() {
        return this.usesWebHardwareScanner() && !this._manualKeyboardMode;
    }

    isInputFocused() {
        const dom = getTextInputDomNode(this.inputRef);
        return !!(dom && typeof document !== "undefined" && document.activeElement === dom);
    }

    ensureInputFocused() {
        if (!this._mounted || this.isInputFocused()) return;
        const dom = getTextInputDomNode(this.inputRef);
        if (!dom) return;
        try {
            dom.focus({ preventScroll: true });
        } catch (_) {
            dom.focus();
        }
    }

    cancelDeferredBlurResolve() {
        if (this._blurResolveTimer) {
            clearTimeout(this._blurResolveTimer);
            this._blurResolveTimer = null;
        }
    }

    clearLocationField() {
        this._suppressBlurResolveUntil = Date.now() + 2000;
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        if (this._refocusTimer) {
            clearTimeout(this._refocusTimer);
            this._refocusTimer = null;
        }
        if (this._blurResolveTimer) {
            clearTimeout(this._blurResolveTimer);
            this._blurResolveTimer = null;
        }
        this._lastScanProcessed = "";
        const dom = getTextInputDomNode(this.inputRef);
        if (dom) {
            try {
                const tracker = dom._valueTracker;
                if (tracker) tracker.setValue("");
                dom.value = "";
            } catch (_) {}
        }
        this.setState({
            value: "",
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
            scanerCanceled: false,
            wedgeInputVersion: this.state.wedgeInputVersion + 1,
        });
    }

    runDeferredBlurResolve() {
        this.cancelDeferredBlurResolve();
        this._blurResolveTimer = setTimeout(() => {
            this._blurResolveTimer = null;
            if (!this._mounted || !this.shouldAllowBlurResolve()) return;
            if (this.isInputFocused()) return;

            const {
                useScanner,
                scannerInput,
                scannerFieldForInput,
                webHardwareScanner,
                defaultLabel,
                showLocationsSearch,
                isShipmentByLocation,
                isTransferByLocation,
                softFocus,
                setShowTextBar,
                id,
                position,
            } = this._blurContext || {};

            const value = cleanScannedText(this.getScanInputValue());
            const wasEdited = this.state.wasEdited;
            const resolveExactOnly = this.shouldUseExactLocationMatch();
            const wasManualEntry = this._manualKeyboardMode || this._humanTypingSession || softFocus;

            if (webHardwareScanner) {
                if (value && wasEdited) {
                    if (this.tryResolveScannedValue(value, {
                        exactOnly: resolveExactOnly && this.shouldUseExactOnlyForTypedValue(value),
                    })) return;
                    if (this.isLocationByForm() && this.useScannerField() && this.usesShipmentStyleLocationScan()) {
                        this.commitUnresolvedLocationText(value, { wasEdited: false });
                        return;
                    }
                }
                if (!this.isInputFocused()) {
                    this._manualKeyboardMode = false;
                    this._humanTypingSession = false;
                    this._lastTypeAt = 0;
                }
                return;
            }

            if (!this.isInputFocused()) {
                this._manualKeyboardMode = false;
                this._humanTypingSession = false;
                this._lastTypeAt = 0;
            }

            if (useScanner && scannerInput && scannerFieldForInput) {
                this.setState({ scanerCanceled: false });
                if (softFocus && !this._manualKeyboardMode && !this.isInputFocused()) {
                    // Receiving-style search must keep soft keyboard available on next focus.
                    if (!this.isReceivingStyleLocationSearch()) {
                        this.setState({ softFocus: false });
                    }
                }
            }
            if (useScanner && setShowTextBar && isMobileWeb()) {
                setShowTextBar(null, null, null, null, null, null, null, false);
            }
            // Receiving-style search: do not fuzzy-rematch typed/deleted text back to a full location on blur.
            if (this.isReceivingStyleLocationSearch()) {
                const { onSelectItem } = this.props;
                if (!value && onSelectItem) onSelectItem("");
                return;
            }
            if (useScanner && value && this.tryResolveScannedValue(value, {
                exactOnly: resolveExactOnly && this.shouldUseExactOnlyForTypedValue(value),
            })) {
                return;
            }
            if (this.isLocationByForm() && this.useScannerField() && this.usesShipmentStyleLocationScan()) {
                const { onSelectItem } = this.props;
                if (!value) {
                    if (onSelectItem) onSelectItem("");
                    return;
                }
                this.commitUnresolvedLocationText(value, { wasEdited: false });
                return;
            }
            if (
                !this.usesShipmentStyleLocationScan() &&
                (defaultLabel == "toLocation" || showLocationsSearch) &&
                value &&
                wasEdited &&
                this.state.filtered?.length <= 0
            ) {
                this.noFoundLocation(value);
            }
        }, BLUR_RESOLVE_MS);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.openBarCodeScanner != this.props.openBarCodeScanner &&
            nextProps.openBarCodeScanner &&
            this.useScannerField()
        ) {
            this.showScanner(true);
        }
        if (nextProps?.options != this.props.options) {
            this.setState({
                items: nextProps.options || [],
            });
        }
        if (
            nextProps?.inventoryLocations != this.props.inventoryLocations &&
            Array.isArray(nextProps.inventoryLocations) &&
            nextProps.inventoryLocations.length &&
            !(Array.isArray(nextProps.options) && nextProps.options.length)
        ) {
            this.setState({
                items: nextProps.inventoryLocations,
            });
        }
        if (
            nextProps.resetInventorySearch?.changed !== this.props.resetInventorySearch?.changed &&
            nextProps.resetInventorySearch?.position === nextProps.position &&
            this.useScannerField()
        ) {
            this.syncFromSubmissionInitial(nextProps.initial, { force: true });
            return;
        }
        if (nextProps.position != this.props.position) {
            this.setState({
                scan: false,
                wasEdited: false,
                openList: false,
                filtered: [],
                scanerCanceled: false,
            });
            //this.syncFromSubmissionInitial(nextProps.initial, { force: true });
            return;
        }
        const initialChanged = !this.locationInitialEquals(nextProps.initial, this.props.initial);
        const tableRerendered = nextProps.rerenderTable !== this.props.rerenderTable;
        if (initialChanged || tableRerendered) {
            if (this.props.defaultLabel == "toLocation" || this.props.showLocationsSearch) {
                this.syncFromSubmissionInitial(nextProps.initial);
            } else if (typeof nextProps?.initial == "object") {
                this.setState({ locationSelected: nextProps?.initial });
            } else if (typeof nextProps?.initial == "string") {
                this.setState({ value: nextProps.initial, wasEdited: false });
            }
        }
        if (nextProps.closeSearch != this.props.closeSearch) {
            this.setState({ filtered: [], wasEdited: false });
        }
    }
    componentWillUnmount() {
        this._mounted = false;
        if (this._interval) clearTimeout(this._interval);
        if (this._resolveTimer) clearTimeout(this._resolveTimer);
        if (this._refocusTimer) clearTimeout(this._refocusTimer);
        if (this._blurResolveTimer) clearTimeout(this._blurResolveTimer);
        if (this._wedgeIdleTimer) clearTimeout(this._wedgeIdleTimer);
        if (this._wedgeSettlePoll) clearTimeout(this._wedgeSettlePoll);
        if (this._wedgeEnterTimer) clearTimeout(this._wedgeEnterTimer);
        this.detachDomScanListeners();
        if (this._onWedgeManualKeyboard && typeof window !== "undefined") {
            window.removeEventListener(WEDGE_MANUAL_KB_EVENT, this._onWedgeManualKeyboard);
        }
        unregisterActiveLocationWedgeField(this.wedgeOwnerId());
        stopSymbolWedgeSessionForOwner(this.wedgeOwnerId(), { immediate: true });
    }

    wedgeOwnerId() {
        const { id, position } = this.props;
        return `location-wedge-${id}-${position}`;
    }

    usesWebHardwareScanner() {
        // Receiving-style searchable destination is typing + dropdown (not Zebra wedge).
        if (this.isReceivingStyleLocationSearch()) return false;
        // Desktop browsers must never enter Zebra/wedge mode (even on narrow windows).
        return Platform.OS === "web" && isMobileWebDevice() && this.useScannerField();
    }

    usesNativeScannerField() {
        // Keep receiving-style location search on soft keyboard / typed search on Android webviews too.
        if (this.isReceivingStyleLocationSearch()) return false;
        return Platform.OS === "android" && this.useScannerField();
    }

    usesHardwareScannerField() {
        return this.usesWebHardwareScanner() || this.usesNativeScannerField();
    }

    isManualTypingMode() {
        if (this.isActiveWedgeField()) return false;
        if (this._humanTypingSession) return true;
        if (this._manualKeyboardMode) return true;
        return false;
    }

    noteKeyboardInputTiming() {
        if (this.isActiveWedgeField()) return;
        const now = Date.now();
        const gap = this._lastTypeAt ? now - this._lastTypeAt : null;
        this._lastTypeAt = now;
        this._suppressBlurResolveUntil = Date.now() + MANUAL_TYPE_DEBOUNCE_MS + 500;
        if (gap != null && gap > 90) {
            this._humanTypingSession = true;
            this.abortPendingWedgeResolve();
        }
    }

    beginManualTypingSession() {
        if (this.isActiveWedgeField()) return;
        this._humanTypingSession = true;
        this.abortPendingWedgeResolve();
    }

    shouldDeferManualTypeResolve(text) {
        if (!text) return true;
        // User is still building a split path (e.g. "office/").
        return /[\/\\]$/.test(text);
    }

    shouldUseExactOnlyForTypedValue(text) {
        if (!text) return true;
        // Paths with separators must match exactly (site1-bin1 ≠ site1).
        return /[\/\\-]/.test(text);
    }

    runManualTypeResolve() {
        const fromInput = cleanScannedText(this.getScanInputValue());
        const fromState = cleanScannedText(this.state.value);
        const current = fromInput || fromState;
        if (!current) return;

        if (this.shouldDeferManualTypeResolve(current)) {
            this.scheduleResolveInput(current);
            return;
        }

        const exactOnly = this.shouldUseExactOnlyForTypedValue(current);
        if (this.tryResolveScannedValue(current, { exactOnly })) return;

        if (this.isLocationByForm() && this.usesShipmentStyleLocationScan()) {
            this.commitUnresolvedLocationText(current, { wasEdited: false });
        }
    }

    abortPendingWedgeResolve() {
        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this._wedgeSettlePoll) {
            clearTimeout(this._wedgeSettlePoll);
            this._wedgeSettlePoll = null;
        }
        if (this._wedgeEnterTimer) {
            clearTimeout(this._wedgeEnterTimer);
            this._wedgeEnterTimer = null;
        }
    }

    shouldUseExactLocationMatch() {
        if (this.usesShipmentStyleLocationScan()) return true;
        return this.isManualTypingMode();
    }

    shouldResolveScanExactOnly(text) {
        if (!text) return true;
        if (this.usesShipmentStyleLocationScan()) return true;
        return /[\/\\-]/.test(text);
    }

    getTypeResolveDelayMs() {
        if (this.isManualTypingMode()) return MANUAL_TYPE_DEBOUNCE_MS;
        const { scannerInput } = this.props;
        return scannerInput ? SCAN_RESOLVE_DELAY_MS : 400;
    }

    getScanInputValue(fallbackTarget) {
        let raw = "";
        if (fallbackTarget && typeof fallbackTarget.value === "string") {
            raw = fallbackTarget.value;
        } else {
            const dom = getTextInputDomNode(this.inputRef);
            if (dom && typeof dom.value === "string") {
                raw = dom.value;
            } else {
                raw = this.state.value;
            }
        }
        return normalizeWedgeScanText(cleanScannedText(raw));
    }

    syncDomScanValue(text) {
        const dom = getTextInputDomNode(this.inputRef);
        if (!dom) return;
        try {
            const tracker = dom._valueTracker;
            if (tracker) tracker.setValue("");
            dom.value = text;
            if (tracker) tracker.setValue(text);
        } catch (_) {}
    }

    focusVisibleScanField({ force = false } = {}) {
        if (!this.usesWebHardwareScanner() || !this._mounted) return;
        if (!force && this.isInputFocused()) return;
        if (!force && this._scanBurstUntil && Date.now() < this._scanBurstUntil) return;
        if (this._focusRetryTimers) {
            this._focusRetryTimers.forEach((t) => clearTimeout(t));
        }
        this._focusRetryTimers = [];
        if (!this._mounted) return;
        if (this._scanBurstUntil && Date.now() < this._scanBurstUntil) return;
        inputFocus(this.inputRef);
        const dom = getTextInputDomNode(this.inputRef);
        if (!dom) return;
        try {
            dom.type = "text";
            dom.inputMode = "text";
            dom.setAttribute("data-wedge-scan", "true");
            dom.setAttribute("enterkeyhint", "done");
        } catch (_) {}
        try {
            dom.focus({ preventScroll: true });
        } catch (_) {
            dom.focus();
        }
    }

    markScanBurst() {
        this._scanBurstUntil = Date.now() + 1500;
        if (this._focusRetryTimers) {
            this._focusRetryTimers.forEach((timer) => clearTimeout(timer));
            this._focusRetryTimers = [];
        }
    }

    /** DOM input value only — never merge parallel buffers (causes doubled chars on Zebra). */
    readDomScanValue(target) {
        const dom = target || getTextInputDomNode(this.inputRef);
        if (dom && typeof dom.value === "string") {
            return cleanScannedText(dom.value);
        }
        return cleanScannedText(this.state.value);
    }

    clearScanBuffer() {
        this._scanBuffer = "";
    }

    releaseWedgeField() {
        this.abortPendingWedgeResolve();
        const ownerId = this.wedgeOwnerId();
        if (getActiveLocationWedgeOwnerId() === ownerId) {
            unregisterActiveLocationWedgeField(ownerId);
        }
        stopSymbolWedgeSessionForOwner(ownerId, { immediate: true });
    }

    armWedgeFromGesture() {
        if (!this.usesWebHardwareScanner() || this.state.scan) return;
        if (this._manualKeyboardMode) return;
        if (this.state.softFocus && !this.isLocationByFormScanField()) return;

        registerActiveLocationWedgeField(this.wedgeOwnerId(), () => {
            this.abortPendingWedgeResolve();
            stopSymbolWedgeSessionForOwner(this.wedgeOwnerId(), { immediate: true });
        });

        const now = Date.now();
        const recentlyArmed = this._lastWedgeArmAt && now - this._lastWedgeArmAt < 300;
        if (!recentlyArmed) {
            this._lastWedgeArmAt = now;
            primeWedgeEnterShield(15000);
            // Enter shield only — scan text comes from the focused input's native input events.
            startSymbolWedgeSession(
                null,
                null,
                this.wedgeOwnerId(),
                null,
                { useHiddenCapture: false }
            );
        }
        extendSymbolScanEnterShield(8000);
        this._suppressBlurResolveUntil = Date.now() + 3000;
        this.attachDomScanListeners();
        this.ensureWedgeFieldFocused();
    }

    ensureWedgeFieldFocused() {
        if (!this.usesWebHardwareScanner() || !this._mounted || this._manualKeyboardMode) return;
        if (getActiveLocationWedgeOwnerId() !== this.wedgeOwnerId()) return;
        if (this.isInputFocused()) return;
        this.focusVisibleScanField({ force: true });
    }

    onDomPreScanPointer() {
        primeWedgeEnterShield(15000);
        if (!this.isInputFocused()) {
            this.focusVisibleScanField({ force: true });
        }
        this.armWedgeFromGesture();
    }

    syncWedgeDomFromTarget(target) {
        const raw = target?.value;
        if (raw == null || raw === "") return false;
        const hasTerminator = /[\r\n\t]/.test(raw);
        if (hasTerminator) {
            this.finishWedgeEnterScan(target);
            return true;
        }
        this.scheduleWedgeIdleComplete();
        return false;
    }

    finishWedgeEnterScan(target) {
        if (this._wedgeEnterTimer) clearTimeout(this._wedgeEnterTimer);
        let attempt = 0;
        let lastSeen = "";
        let stableCount = 0;
        const tryRead = () => {
            if (!this._mounted) return;
            const scanned = this.readDomScanValue(target);
            if (scanned && !isGarbledWedgeScanText(scanned) && !(/^0{2,}\d*[A-Za-z]{1,4}$/.test(scanned) && !/[-/]/.test(scanned))) {
                if (scanned === lastSeen) {
                    stableCount += 1;
                } else {
                    lastSeen = scanned;
                    stableCount = 0;
                }
                if (stableCount >= 2) {
                    this.processHardwareScan(scanned);
                    return;
                }
            }
            if (attempt >= WEDGE_ENTER_READ_DELAYS_MS.length) {
                if (lastSeen) {
                    this.processHardwareScan(lastSeen);
                } else {
                    this.scheduleWedgeIdleComplete();
                }
                return;
            }
            const delay = WEDGE_ENTER_READ_DELAYS_MS[attempt++];
            this._wedgeEnterTimer = setTimeout(tryRead, delay);
        };
        this._wedgeEnterTimer = setTimeout(tryRead, 80);
    }

    onDomKeyDown(e) {
        if (!this.usesWebHardwareScanner()) return;
        const isEnter = e.key === "Enter" || e.key === "Return" || e.keyCode === 13;
        const isTab = e.key === "Tab" || e.keyCode === 9;
        if (isEnter || isTab) {
            e.preventDefault();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            if (e.stopPropagation) e.stopPropagation();
            extendSymbolScanEnterShield(8000);
            this.markScanBurst();
            this.finishWedgeEnterScan(e.target);
            return;
        }
        // Do not touch DOM / setState on printable keys — only buffer via beforeinput/input.
    }

    handleWedgeDomInput(e) {
        if (!this.usesWebHardwareScanner() || this._manualKeyboardMode) return;
        this.markScanBurst();
        this.syncWedgeDomFromTarget(e?.target);
    }

    attachDomScanListeners() {
        if (!this.usesWebHardwareScanner()) return;

        const dom = getTextInputDomNode(this.inputRef);
        if (!dom || dom === this._wedgeDom) return;
        this.detachDomScanListeners();
        this._wedgeDom = dom;

        this._onDomPreScanPointer = () => this.onDomPreScanPointer();
        this._onDomWedgeInput = (e) => this.handleWedgeDomInput(e);
        this._onDomKeyDown = (e) => this.onDomKeyDown(e);
        dom._gohaccpWedgeEnterHandler = () => this.finishWedgeEnterScan(dom);
        dom.addEventListener("pointerdown", this._onDomPreScanPointer, true);
        dom.addEventListener("touchstart", this._onDomPreScanPointer, true);
        dom.addEventListener("input", this._onDomWedgeInput, true);
        dom.addEventListener("keydown", this._onDomKeyDown, true);
        try {
            dom.type = "text";
            dom.setAttribute("enterkeyhint", "done");
            dom.setAttribute("data-wedge-scan", "true");
            dom.inputMode = "text";
        } catch (_) {}
    }

    detachDomScanListeners() {
        if (!this._wedgeDom) return;
        if (this._onDomPreScanPointer) {
            this._wedgeDom.removeEventListener("pointerdown", this._onDomPreScanPointer, true);
            this._wedgeDom.removeEventListener("touchstart", this._onDomPreScanPointer, true);
        }
        if (this._onDomWedgeInput) {
            this._wedgeDom.removeEventListener("input", this._onDomWedgeInput, true);
        }
        if (this._onDomKeyDown) {
            this._wedgeDom.removeEventListener("keydown", this._onDomKeyDown, true);
        }
        try {
            delete this._wedgeDom._gohaccpWedgeEnterHandler;
        } catch (_) {}
        this._wedgeDom = null;
        this._onDomPreScanPointer = null;
        this._onDomWedgeInput = null;
        this._onDomKeyDown = null;
    }

    scheduleWedgeIdleComplete() {
        if (!this.usesWebHardwareScanner() && !this.usesNativeScannerField()) return;
        if (this._wedgeIdleTimer) clearTimeout(this._wedgeIdleTimer);
        if (this._wedgeSettlePoll) clearTimeout(this._wedgeSettlePoll);

        const startedAt = Date.now();
        let lastLen = 0;
        let lastChangeAt = Date.now();

        const poll = () => {
            this._wedgeSettlePoll = null;
            if (!this._mounted) return;

            const scanned = this.readDomScanValue();
            const now = Date.now();
            const len = scanned ? scanned.length : 0;

            if (len > lastLen) {
                lastLen = len;
                lastChangeAt = now;
            }

            const stable = scanned && len >= 2 && (now - lastChangeAt >= WEDGE_SETTLE_STABLE_MS);
            const timedOut = now - startedAt >= WEDGE_SETTLE_MAX_MS;

            if (stable || (timedOut && scanned && len >= 2)) {
                if (isGarbledWedgeScanText(scanned)) return;
                if (/^0{2,}\d*[A-Za-z]{1,4}$/.test(scanned) && !/[-/]/.test(scanned)) return;
                this.processHardwareScan(scanned);
                return;
            }

            if (!timedOut) {
                this._wedgeSettlePoll = setTimeout(poll, WEDGE_SETTLE_POLL_MS);
            }
        };

        this._wedgeSettlePoll = setTimeout(poll, WEDGE_SETTLE_POLL_MS);
    }

    enableManualKeyboard() {
        if (!this.usesWebHardwareScanner()) return;
        this._manualKeyboardMode = true;
        this._humanTypingSession = true;
        this.releaseWedgeField();
        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this._wedgeSettlePoll) {
            clearTimeout(this._wedgeSettlePoll);
            this._wedgeSettlePoll = null;
        }
        if (this._wedgeEnterTimer) {
            clearTimeout(this._wedgeEnterTimer);
            this._wedgeEnterTimer = null;
        }
        this.setState({ softFocus: true }, () => {
            this.focusVisibleScanField();
            const dom = getTextInputDomNode(this.inputRef);
            if (dom) {
                try {
                    dom.inputMode = "text";
                } catch (_) {}
            }
        });
    }

    pauseWedgeHardware() {
        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this._wedgeSettlePoll) {
            clearTimeout(this._wedgeSettlePoll);
            this._wedgeSettlePoll = null;
        }
        if (this._wedgeEnterTimer) {
            clearTimeout(this._wedgeEnterTimer);
            this._wedgeEnterTimer = null;
        }
        if (this.usesWebHardwareScanner()) {
            this.releaseWedgeField();
        }
    }

    resumeWedgeHardware() {
        if (!this._mounted || !this.usesWebHardwareScanner() || this.state.scan) return;
        if (!this._manualKeyboardMode && this.isInputFocused()) {
            this.armWedgeFromGesture();
        }
    }

    syncWedgeInputValue(clean) {
        if (!clean) return;
        this.syncDomScanValue(clean);
        if (this.state.value !== clean || !this.state.wasEdited) {
            this.setState({ value: clean, wasEdited: true });
        }
    }

    processHardwareScan(raw) {
        const clean = normalizeWedgeScanText(dedupeDoubledWedgeScan(cleanScannedText(raw)));
        if (!clean || !this._mounted) return;
        if (isGarbledWedgeScanText(clean)) return;
        // Reject Sincoole keydown garbage (e.g. "000026sie" / "sie").
        if (/^0{2,}\d*[A-Za-z]{1,4}$/.test(cleanScannedText(raw)) && !/[-/]/.test(cleanScannedText(raw))) {
            return;
        }
        if (clean.length < 2) return;

        const now = Date.now();
        if (this._lastScanProcessed && now - (this._lastScanProcessedAt || 0) < 500) {
            if (clean.length <= this._lastScanProcessed.length) return;
            // Prefer longer barcode if a partial committed first (first-scan truncation).
            if (!clean.includes(this._lastScanProcessed) && !this._lastScanProcessed.includes(clean)) {
                // Allow upgrade when fragments merge into a longer valid barcode.
            }
        } else if (clean === this._lastScanProcessed && now - (this._lastScanProcessedAt || 0) < 200) {
            return;
        }
        this._lastScanProcessed = clean;
        this._lastScanProcessedAt = now;
        this._humanTypingSession = false;
        this._lastTypeAt = 0;
        this._scanBurstUntil = 0;
        this.clearScanBuffer();
        clearPassiveWedgeCaptureValue();
        extendSymbolScanEnterShield(8000);

        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this._wedgeSettlePoll) {
            clearTimeout(this._wedgeSettlePoll);
            this._wedgeSettlePoll = null;
        }
        if (this._wedgeEnterTimer) {
            clearTimeout(this._wedgeEnterTimer);
            this._wedgeEnterTimer = null;
        }
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }

        this.setState({
            value: clean,
            wasEdited: true,
        }, () => {
            if (!this._mounted) return;
            this.syncDomScanValue(clean);
            const exactOnly = this.shouldResolveScanExactOnly(clean);
            if (!this.tryResolveScannedValue(clean, { exactOnly })) {
                if (this.usesShipmentStyleLocationScan()) {
                    this.commitUnresolvedLocationText(clean, { wasEdited: false });
                } else if (this.props.isShipmentByLocation || this.props.isTransferByLocation || this.props.isInventoryTransfer) {
                    this.searchItems(clean, false);
                }
            }
            if (this.usesWebHardwareScanner()) {
                extendSymbolScanEnterShield(8000);
            }
        });
    }

    getLocationOptions() {
        const { options, inventoryLocations } = this.props;
        const { items } = this.state;
        if (Array.isArray(options) && options.length) return options;
        if (Array.isArray(inventoryLocations) && inventoryLocations.length) return inventoryLocations;
        if (Array.isArray(items) && items.length) return items;
        return [];
    }

    shouldAutoOpenCameraOnFocus() {
        const { scannerInput, isShipmentByLocation, isTransferByLocation, showLocationsSearch, defaultLabel } = this.props;
        /*
         * Shipment-by-location / transfer-by-location table rows: never auto-open the camera on focus.
         * Row 1 often already has a value (so camera stayed off); empty row 2 used to
         * auto-open the camera on Zebra / mobile web and crash the app. Use the hardware
         * wedge or the KeyboardBar scan button instead.
         */
        if (isShipmentByLocation && showLocationsSearch && defaultLabel === "location") {
            return false;
        }
        if (isTransferByLocation && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) {
            return false;
        }
        // Receiving-style location search: searchable field — camera is button-only (keep soft keyboard).
        if (this.isReceivingStyleLocationSearch()) return false;
        if (!scannerInput || !this.scannerFieldActive()) return false;
        if (Platform.OS === "android") return false;
        return Platform.OS === "ios" || (Platform.OS === "web" && isMobileWeb());
    }

    showScanAlert(message) {
        if (Platform.OS === "web") {
            Swal.fire({
                text: message,
                confirmButtonText: "Accept",
                confirmButtonColor: colors.orange,
                backdrop: false,
            });
            return;
        }
        Alert.alert("", message, [{ text: "Accept" }]);
    }

    handleUnresolvedShipmentScan(raw) {
        this.commitUnresolvedLocationText(raw, { wasEdited: true });
    }

    commitUnresolvedLocationText(raw, { wasEdited = false, updateForm = true } = {}) {
        const { onSelectItem } = this.props;
        const clean = parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
        if (!clean || !this._mounted) return;

        const resolved = this.resolveLocationForScan(clean);
        if (resolved) {
            this.selectLocation(resolved, { skipForm: !updateForm });
            return;
        }

        const hideList = this.shouldHideLocationSearchList();
        this.setState({
            value: clean,
            wasEdited,
            openList: hideList ? false : true,
            filtered: [],
            scan: false,
            wedgeInputVersion: this.state.wedgeInputVersion + 1,
        }, () => {
            this.syncDomScanValue(clean);
            if (!hideList) {
                this.searchItems(clean, false);
            }
            if (updateForm && onSelectItem) {
                onSelectItem(clean);
            }
            if (updateForm && this.usesShipmentStyleLocationScan()) {
                this.triggerInventoryFetch(clean, resolved);
            }
        });
    }

    useScannerField() {
        const { isShipmentByLocation, isTransferByLocation, showLocationsSearch, defaultLabel, isInventoryTransfer, isReceivingLog } = this.props;
        if (this.isLocationByFormScanField()) return true;
        if (isInventoryTransfer && !isTransferByLocation && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) {
            return true;
        }
        if (isReceivingLog && defaultLabel === "toLocation") return true;
        if (this.isYieldLocationSearch()) return true;
        if (isShipmentByLocation && showLocationsSearch && defaultLabel === "location") return true;
        if (
            Platform.OS === "web" &&
            isMobileWeb() &&
            showLocationsSearch &&
            (defaultLabel === "location" || defaultLabel === "fromLocation" || defaultLabel === "toLocation")
        ) {
            return true;
        }
        return false;
    }

    usesWedgeDomInput() {
        return this.usesWebHardwareScanner();
    }

    scannerFieldActive() {
        const { showScanner, scannerInput } = this.props;
        return showScanner || (this.useScannerField() && scannerInput);
    }

    /** Shipment/transfer location scanner fields never show an autocomplete dropdown. */
    shouldHideLocationSearchList() {
        // Receiving-style searchable fields keep the suggestion list visible.
        if (this.isReceivingStyleLocationSearch()) return false;
        return !!(this.useScannerField() && this.usesShipmentStyleLocationScan());
    }

    usesShipmentLocationResolve() {
        return !!(this.useScannerField() && this.usesShipmentStyleLocationScan());
    }

    showScanner(value) {
        if (value) {
            this.pauseWedgeHardware();
        }
        this.setState({ scan: value, scanerCanceled: value ? false : this.state.scanerCanceled }, () => {
            if (!value) {
                setTimeout(() => this.resumeWedgeHardware(), 300);
            }
        });
    }

    showScannerInput() {
        const { setFormScroll } = this.props;
        if (!this.state.scanerCanceled) {
            if (setFormScroll) setFormScroll(false);
            setTimeout(() => {
                this.showScanner(true);
                setTimeout(() => {
                    if (setFormScroll) setFormScroll(true);
                }, 300);
            }, 500);
        } else {
            this.setState({ scanerCanceled: false });
        }
    }

    canShowCameraButton() {
        const { defaultComponent, scannerInput, showScanner } = this.props;
        if (!(this.isLocationByFormScanField() || this.isReceivingStyleLocationSearch())) return false;
        if (!this.useScannerField()) return false;
        if (defaultComponent) return false;
        if (!(showScanner || scannerInput)) return false;
        if (Platform.OS === "ios") return false;
        return Platform.OS !== "web" || isMobileWeb();
    }

    useCompactMobileLayout() {
        // Yield location uses dedicated table-cell styling to match Lot/Amount heights.
        if (this.isYieldLocationSearch()) return false;
        // Receiving-style location search in a table cell: always single-line + optional camera row.
        if (this.isReceivingStyleLocationSearch() && this.props.width) return true;
        if (this.isReceivingStyleLocationSearch()) {
            return Platform.OS === "android" || Platform.OS === "ios" || (Platform.OS === "web" && isMobileWeb());
        }
        if (!this.useScannerField()) return false;
        if (!this.isLocationByForm()) return false;
        // Compact camera layout is for location-by-form scan UIs, not classic inventory-transfer search.
        if (!this.usesShipmentStyleLocationScan()) return false;
        return Platform.OS === "android" || (Platform.OS === "web" && isMobileWeb());
    }

    openCameraScanner() {
        const { setFormScroll } = this.props;
        this._suppressBlurResolveUntil = Date.now() + 15000;
        this.cancelDeferredBlurResolve();
        this.pauseWedgeHardware();
        if (setFormScroll) setFormScroll(false);
        this.showScanner(true);
    }

    findLocationByScan(data, { exactOnly = false } = {}) {
        return findLocationInList(data, this.getLocationOptions(), { exactOnly });
    }

    /** Resolve camera/laser text to a full location object (required for inventory fetch). */
    resolveLocationForScan(raw) {
        const options = this.getLocationOptions();
        if (!Array.isArray(options) || !options.length) return null;

        const candidates = [];
        const seen = new Set();
        const addCandidate = (text) => {
            const clean = cleanScannedText(parseScannedValue(text));
            if (!clean || seen.has(clean)) return;
            seen.add(clean);
            candidates.push(clean);
            const normalized = normalizeWedgeScanText(clean);
            if (normalized && normalized !== clean && !seen.has(normalized)) {
                seen.add(normalized);
                candidates.push(normalized);
            }
        };
        addCandidate(raw);

        for (let i = 0; i < candidates.length; i++) {
            const text = candidates[i];
            const match =
                findLocationInList(text, options, { exactOnly: true }) ||
                findLocationInList(text, options, { exactOnly: false });
            if (match) return match;
        }
        return null;
    }

    tryResolveScannedValue(raw, { updateForm = true, exactOnly = false } = {}) {
        const clean = normalizeWedgeScanText(cleanScannedText(parseScannedValue(raw)));
        if (!clean) return false;
        const match = this.resolveLocationForScan(clean) || this.findLocationByScan(clean, { exactOnly });
        if (match) {
            this.selectLocation(match, { skipForm: !updateForm });
            return true;
        }
        return false;
    }

    scheduleResolveInput(raw) {
        const useScanner = this.useScannerField();
        if (!useScanner) return;
        // Receiving-style search: never auto-resolve while typing (lets users delete/edit freely).
        if (this.isReceivingStyleLocationSearch()) return;
        // Desktop web: resolve only on blur so users can type/delete freely.
        // Mobile devices keep live resolve for soft-keyboard + Zebra workflows.
        if (Platform.OS === "web" && !isMobileWebDevice()) return;

        if (this._resolveTimer) clearTimeout(this._resolveTimer);

        this._resolveTimer = setTimeout(() => {
            this._resolveTimer = null;
            if (!this._mounted) return;
            this.runManualTypeResolve();
        }, MANUAL_TYPE_DEBOUNCE_MS);
    }

    finishScanProcessing(data) {
        if (!this._mounted || !data) return;
        this._suppressBlurResolveUntil = Date.now() + 2000;
        this.cancelDeferredBlurResolve();
        const clean = normalizeWedgeScanText(cleanScannedText(parseScannedValue(data)));
        if (!clean) return;
        try {
            var location = this.resolveLocationForScan(data);
            if (location) {
                this.selectLocation(location);
            } else if (this.useScannerField() && this.usesShipmentStyleLocationScan()) {
                this.commitUnresolvedLocationText(clean);
            }
            return;
        } catch (err) {
            console.warn("SearchInventoryLocations finishScanProcessing:", err);
            if (!(this.useScannerField() && this.usesShipmentStyleLocationScan())) {
                this.showScanAlert("Unable to process the scanned location. Please try again or search manually.");
            }
        }
    }

    handleBarCodeScanned(raw) {
        const data = parseScannedValue(raw);
        if (!this._mounted || this._handlingScan) return;
        this._handlingScan = true;
        this._suppressBlurResolveUntil = Date.now() + 3000;
        this.cancelDeferredBlurResolve();
        this.pauseWedgeHardware();
        this.setState({ scan: false });
        const settleMs = Platform.OS === "web" ? 450 : 250;
        setTimeout(() => {
            this._handlingScan = false;
            if (!this._mounted) return;
            this.finishScanProcessing(data);
            setTimeout(() => this.resumeWedgeHardware(), 300);
        }, settleMs);
    }
    searchItems(val, focused, { autoSelect = null } = {}) {
        const items = this.getLocationOptions();
        const usesShipmentScan = this.usesShipmentStyleLocationScan();
        const hideList = this.shouldHideLocationSearchList();
        const allowAutoSelect = autoSelect === null
            ? (usesShipmentScan && !this.useScannerField() && !this.isManualTypingMode())
            : autoSelect;
        var search = val;
        if (this._interval) clearTimeout(this._interval);
        if (search) {
            search = search.toLowerCase().trim();
            if (usesShipmentScan && allowAutoSelect) {
                var resolved = this.findLocationByScan(search);
                if (resolved) {
                    this.selectLocation(resolved);
                    return;
                }
            }
            var filtered = items.filter((item) => locationListIncludesTerm([item], search));
            if (this.isLocationByForm() && usesShipmentScan) filtered = filtered.slice(0, 10);
            this.setState({
                filtered: hideList ? [] : filtered,
                openList: hideList ? false : true,
                wasEdited: hideList ? this.state.wasEdited : true,
            });

        } else {
            var filtered = [];
            if (this.isLocationByForm() && usesShipmentScan) filtered = items.slice(0, 10);
            else filtered = items;
            this.setState({ filtered: hideList ? [] : filtered, wasEdited: focused ? true : false, openList: hideList ? false : true });
        }
    }
    noFoundLocation(search) {
        const { onSelectItem } = this.props;
        search = search.toLowerCase().trim();
        var filtered = this.state.items.filter((item) => {
            var completeLocation = "";
            if (item?.site?.locationName) {
                completeLocation += item.site.locationName;
            }
            if (item?.zone?.locationName) {
                completeLocation += "-" + item.zone.locationName;
            }
            if (item?.aisle?.locationName) {
                completeLocation += "-" + item.aisle.locationName;
            }
            if (item?.rackBay?.locationName) {
                completeLocation += "-" + item.rackBay.locationName;
            }
            if (item?.shelfLevel?.locationName) {
                completeLocation += "-" + item.shelfLevel.locationName;
            }
            if (item?.bin?.locationName) {
                completeLocation += "-" + item.bin.locationName;
            }
            completeLocation = completeLocation.toLowerCase().trim();
            return completeLocation.includes(search);
        });
        if (filtered.length <= 0)
            Swal.fire({
                text: "This location does not exist. A new main location will be created automatically on sending the form, but it must be reorganized by an administrator. This is not recommended. Do you want to proceed?",
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                showCancelButton: true,
                confirmButtonColor: colors.orange,
                backdrop: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.setState({ value: search, filtered: [], openList: false });
                    onSelectItem(search);
                } else {
                    this.setState({ value: "", filtered: [], openList: false });
                    onSelectItem("");
                }
            });
    }
    notifyLocationResolved(item) {
        const { onLocationResolved, isReceivingLog, defaultLabel, isInventoryTransfer, isTransferByLocation } = this.props;
        if (!onLocationResolved || item == null || item === "") return;
        if (isReceivingLog && defaultLabel === "toLocation") return;
        if (isInventoryTransfer && !isTransferByLocation && (defaultLabel === "toLocation" || defaultLabel === "fromLocation")) return;
        try {
            onLocationResolved(item);
        } catch (err) {
            console.warn("SearchInventoryLocations notifyLocationResolved:", err);
        }
    }

    triggerInventoryFetch(clean, location) {
        if (!clean && !location) return;
        const resolved = location || this.resolveLocationForScan(clean) || clean;
        this.notifyLocationResolved(resolved);
    }

    selectLocation(item, { skipForm = false } = {}) {
        const { onSelectItem } = this.props;
        const label = renderLocationLabel(item);
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        if (!skipForm && onSelectItem && this._mounted && item != null && item !== "") {
            if (typeof item === "string") {
                onSelectItem(item);
            } else {
                onSelectItem([item]);
            }
        }
        if (!skipForm && typeof item === "object" && item != null) {
            this.notifyLocationResolved(item);
        }
        this._suppressBlurResolveUntil = Date.now() + 2000;
        this.setState({
            value: label,
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
            wedgeInputVersion: this.state.wedgeInputVersion + 1,
        }, () => {
            this.syncDomScanValue(label);
        });
    }

    renderLocation(location) {
        return renderLocationLabel(location);
    }

    openFromLocationModal() {
        this.setState({ showFromLocationModal: true });
    }

    /** Completed transfers are view-only unless reopened as edit_required. */
    isInventoryTransferModalReadOnly() {
        const { complete_date, defaultComponent, submissionStatus } = this.props;
        if (submissionStatus === "edit_required") return false;
        if (complete_date) return true;
        return !!defaultComponent;
    }

    getRowFieldValue(defaultLabels) {
        const { components, submission, position, id } = this.props;
        if (!components || !submission) return null;
        const self = components.find((c) => c.id == id);
        const section_id = self?.section_id;
        if (section_id == null) return null;
        const labels = Array.isArray(defaultLabels) ? defaultLabels : [defaultLabels];
        const component = components.find(
            (c) => c.section_id == section_id && labels.includes(c.defaultLabel)
        );
        if (!component) return null;
        const raw = submission[component.id];
        if (raw == null) return null;
        if (position || position == 0) return raw[position];
        return Array.isArray(raw) ? raw[0] : raw;
    }

    onFromLocationModalSave(rows) {
        const { onInventoryTransferLocationSave, position } = this.props;
        this.setState({ showFromLocationModal: false });
        if (onInventoryTransferLocationSave) {
            onInventoryTransferLocationSave(rows, position);
        }
    }

    render() {
        const { value, items, wasEdited, filtered, openList, scan, scanerCanceled, softFocus, inputAccessoryViewID, wedgeInputVersion, showFromLocationModal } = this.state;
        const { defaultComponent, onSelectItem, placeholder, customers, width, isRecipe, openDropDown, backgroundColor, isLast, defaultLabel, isDropDownOpened, resetDropDown, onFocus, setShowTextBar, showLocationsSearch, isShipmentByLocation, isTransferByLocation, handleFocusOnElement, setFormScroll, id, position, scannerInput, showScanner, isReceivingLog, isInventoryTransfer, inventoryTableDesign, inventoryLocations, complete_date } = this.props;
        const useScanner = this.useScannerField();
        const scannerFieldForInput = this.scannerFieldActive();
        const webHardwareScanner = this.usesWebHardwareScanner();
        const hardwareScannerField = this.usesHardwareScannerField();
        const showCameraButton = this.canShowCameraButton();
        const compactMobile = this.useCompactMobileLayout();
        const laserUncontrolled = webHardwareScanner && !this._manualKeyboardMode;
        const transferSearchPill = !!(isInventoryTransfer && inventoryTableDesign && !isTransferByLocation);
        const transferFromLocationPill = !!(transferSearchPill && defaultLabel === "fromLocation");
        /** Transfer to is set only inside the Current Location modal — read-only on the form. */
        const transferToLocationReadOnly = !!(transferSearchPill && defaultLabel === "toLocation");
        const rawFromLocation =
          unwrapLocationValue(this.getRowFieldValue("fromLocation")) ||
          unwrapLocationValue(this.props.initial);
        const fromLocationValue =
          resolveTransferLocation(rawFromLocation, inventoryLocations || this.props.options || []) ||
          rawFromLocation;
        const toLocationValue = unwrapLocationValue(this.getRowFieldValue("toLocation"));
        const initialItem = this.getRowFieldValue(["itemName", "item", "ingredient", "name"]);
        const initialLotcodes = this.getRowFieldValue("lotcode");
        const fromLocationPillLabel =
          getTransferLocationPathLabel(fromLocationValue) ||
          renderLocationLabel(fromLocationValue) ||
          value ||
          "Search";
        const toLocationPillLabel =
          renderLocationLabel(toLocationValue) ||
          renderLocationLabel(unwrapLocationValue(this.props.initial)) ||
          value ;
        const hasSelectedFromLocation = !!(fromLocationValue || (value && String(value).trim()));
        const hasSelectedToLocation = !!(
          toLocationValue ||
          unwrapLocationValue(this.props.initial) ||
          (value && String(value).trim())
        );
        const transferPillHasData =
          (defaultLabel === "fromLocation" && hasSelectedFromLocation) ||
          (defaultLabel === "toLocation" && hasSelectedToLocation) ||
          (defaultLabel !== "fromLocation" && defaultLabel !== "toLocation" && value && String(value).trim());

        const scanInput = (
            <TextInput
                {...(webHardwareScanner ? {} : getNoAutofillProps())}
                {...(laserUncontrolled
                    ? { defaultValue: value, key: `laser-${id}-${position}-${wedgeInputVersion}` }
                    : { value })}
                ref={(el) => {
                    if (webHardwareScanner) {
                        scannerFieldRef(this.inputRef)(el);
                    } else {
                        noAutofillRef(this.inputRef)(el);
                    }
                    if (el) {
                        this.attachDomScanListeners();
                    }
                }}
                editable={!defaultComponent && !(useScanner && scannerInput && scannerFieldForInput && !value && !scanerCanceled && Platform.OS === "ios" && !this.isReceivingStyleLocationSearch())}
                showSoftInputOnFocus={!defaultComponent && (Platform.OS === "ios" || softFocus || this.isReceivingStyleLocationSearch())}
                blurOnSubmit={webHardwareScanner ? false : undefined}
                returnKeyType={webHardwareScanner ? "done" : undefined}
                onSubmitEditing={(e) => {
                    if (!webHardwareScanner) return;
                    const dom = getTextInputDomNode(this.inputRef);
                    this.finishWedgeEnterScan(dom || e?.target);
                }}
                onPressIn={webHardwareScanner ? () => {
                    primeWedgeEnterShield(15000);
                    this.armWedgeFromGesture();
                } : undefined}
                onMouseDown={webHardwareScanner ? (e) => {
                    if (e?.button !== 0) return;
                    primeWedgeEnterShield(15000);
                    this.armWedgeFromGesture();
                } : undefined}
                onLongPress={webHardwareScanner ? () => this.enableManualKeyboard() : undefined}
                onPress={() => {
                    if (webHardwareScanner && !this._manualKeyboardMode) {
                        this.ensureWedgeFieldFocused();
                    }
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                    }
                    if (Platform.OS === "web" && isMobileWeb() && !defaultComponent && onFocus) {
                        onFocus();
                    }
                }}
                onFocus={() => {
                    this.cancelDeferredBlurResolve();
                    if (this.isReceivingStyleLocationSearch()) {
                        this._manualKeyboardMode = true;
                        this._humanTypingSession = true;
                        if (!this.state.softFocus) this.setState({ softFocus: true });
                        this.beginManualTypingSession();
                    } else if (webHardwareScanner) {
                        this.armWedgeFromGesture();
                        extendSymbolScanEnterShield(8000);
                    } else if (hardwareScannerField) {
                        this.ensureInputFocused();
                    } else if (useScanner && scannerFieldForInput) {
                        this.beginManualTypingSession();
                    }
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                        return;
                    }
                    const skipParentScannerReopen = useScanner && scannerInput && scannerFieldForInput && !value && scanerCanceled;
                    if (onFocus && !skipParentScannerReopen && !webHardwareScanner) {
                        onFocus();
                    }
                    if (!defaultComponent && !value && !(useScanner && scannerFieldForInput)) {
                        this.searchItems("", true);
                    }
                    // Receiving-style location search: open filtered suggestions while the user types/selects.
                    if (!defaultComponent && this.isReceivingStyleLocationSearch()) {
                        this.searchItems(value || "", true);
                        // Ensure soft keyboard on mobile web.
                        setTimeout(() => this.ensureInputFocused(), 0);
                    }
                    if (
                        useScanner &&
                        setShowTextBar &&
                        isMobileWeb() &&
                        !webHardwareScanner &&
                        !this.useScannerField()
                    ) {
                        setShowTextBar(id, position, [], null, null, null, null, false, true);
                    } else if (
                        setShowTextBar &&
                        (isMobileWeb() || Platform.OS === "android") &&
                        !webHardwareScanner &&
                        !this.useScannerField()
                    ) {
                        setShowTextBar(null, null, null, null, null, null, null, false);
                    }
                }}
                onContentSizeChange={(event) => {
                    if (compactMobile || this.isReceivingStyleLocationSearch()) return;
                    if (event.nativeEvent.contentSize.height == 0) {
                        if (this.props.placeholder == '') {
                            this.setState({ height: 30 })
                        }
                        this.setState({ height: event.nativeEvent.contentSize.height });
                    } else {
                        setTimeout(() => {
                            this.setState({ height: event.nativeEvent.contentSize.height });
                        }, 5)
                    }
                }}
                onBlur={() => {
                    if (webHardwareScanner && !this._manualKeyboardMode) {
                        const ownerId = this.wedgeOwnerId();
                        setTimeout(() => {
                            if (!this._mounted || this.isInputFocused()) return;
                            if (getActiveLocationWedgeOwnerId() !== ownerId) return;
                            this.releaseWedgeField();
                        }, 120);
                    }
                    this._blurContext = {
                        useScanner,
                        scannerInput,
                        scannerFieldForInput,
                        webHardwareScanner,
                        defaultLabel,
                        showLocationsSearch,
                        isShipmentByLocation,
                        softFocus,
                        setShowTextBar,
                        id,
                        position,
                    };
                    this.runDeferredBlurResolve();
                }}
                inputAccessoryViewID={useScanner ? inputAccessoryViewID : undefined}
                style={compactMobile ? {
                    ...styles.compactInput,
                    fontStyle: this.state.value ? this.state.value.length == 0 ? 'italic' : 'normal' : 'normal',
                } : transferSearchPill ? {
                    ...styles.input,
                    borderColor: transferPillHasData ? colors.orange : colors.primary,
                    borderRadius: 20,
                    borderWidth: transferPillHasData ? 1.5 : 0,
                    color: transferPillHasData ? "#222" : "#fff",
                    backgroundColor: transferPillHasData ? "#fff" : colors.primary,
                    fontFamily: "Roboto",
                    fontSize: 14,
                    fontWeight: "400",
                    padding: 8,
                    height: 36,
                    minWidth: 0,
                    textAlign: "center",
                    overflow: "hidden",
                } : {
                    ...styles.input,
                    ...(showCameraButton ? styles.inputWithCamera : null),
                    borderColor: this.isYieldLocationSearch() ? colors.orange : "#e7e7e7",
                    borderRadius: this.isYieldLocationSearch() ? 10 : 5,
                    borderWidth: 1,
                    color: this.isYieldLocationSearch() ? colors.orange : "#424242",
                    fontFamily: "Roboto",
                    fontSize: this.isYieldLocationSearch() ? 14 : 20,
                    fontWeight: "400",
                    padding: 5,
                    // Yield location matches Lot Code cell height; receiving toLocation keeps 42.
                    height: this.isYieldLocationSearch()
                      ? (Platform.OS === "ios" ? 44 : 50)
                      : (this.isReceivingToLocation() ? 42 : this.state.height),
                    marginTop: this.isYieldLocationSearch() ? 15 : 0,
                    minWidth: 0,
                    textAlign: this.isYieldLocationSearch() ? "center" : "left",
                    fontStyle: this.state.value ? this.state.value.length == 0 ? 'italic' : 'normal' : 'normal',
                    overflow: 'hidden'
                }}
                multiline={compactMobile || this.isReceivingStyleLocationSearch() || transferSearchPill ? false : !webHardwareScanner}
                numberOfLines={1}
                placeholder={transferSearchPill ? "Search" : (this.isReceivingStyleLocationSearch() ? (placeholder || "Search locations") : placeholder)}
                placeholderTextColor={transferSearchPill ? (transferPillHasData ? "#222" : "#fff") : 'rgb(0, 47, 108)'}
                onChangeText={(val) => {
                    if (webHardwareScanner) {
                        if (isIncompleteWedgePrefix(val)) {
                            this.syncDomScanValue("");
                        }
                        return;
                    }

                    const raw = parseScannedValue(val);
                    const hasTerminator = /[\r\n\t]/.test(raw);
                    const cleanVal = cleanScannedText(raw);

                    if (hardwareScannerField && hasTerminator && cleanVal) {
                        this.processHardwareScan(cleanVal);
                        return;
                    }

                    if (hardwareScannerField && !webHardwareScanner) {
                        if (cleanVal && !isGarbledWedgeScanText(cleanVal)) {
                            this.syncWedgeInputValue(cleanVal);
                        }
                        this.scheduleWedgeIdleComplete();
                        return;
                    }

                    this.noteKeyboardInputTiming();

                    if (useScanner && this.scannerFieldActive() && !hardwareScannerField) {
                        this.beginManualTypingSession();
                    }

                    if (cleanVal == '' && this.props.placeholder == '') {
                        this.setState({ value: cleanVal, height: 30, wasEdited: true, openList: false, filtered: [] });
                        if (this.usesShipmentLocationResolve() && onSelectItem) onSelectItem("");
                    } else if (this.usesShipmentLocationResolve()) {
                        this.setState({ value: cleanVal, wasEdited: true, openList: false, filtered: [] });
                        // Desktop: keep form in sync when clearing so props don't restore the old label.
                        if (!cleanVal && onSelectItem && Platform.OS === "web" && !isMobileWebDevice()) {
                            onSelectItem("");
                        }
                    } else {
                        const hideListWhileTyping = this.shouldHideLocationSearchList();
                        this.setState({
                            value: cleanVal,
                            wasEdited: true,
                            ...(hideListWhileTyping ? { openList: false, filtered: [] } : null),
                        });
                    }

                    if (this.usesShipmentLocationResolve()) {
                        if (cleanVal !== '') {
                            this.scheduleResolveInput(cleanVal);
                        }
                        return;
                    }

                    if (useScanner && this.scannerFieldActive() && this.shouldHideLocationSearchList()) {
                        this.scheduleResolveInput(cleanVal);
                        return;
                    }

                    if (this._interval) clearTimeout(this._interval);
                    const searchDelay = this.isManualTypingMode()
                        ? MANUAL_TYPE_DEBOUNCE_MS
                        : TYPE_SEARCH_DEBOUNCE_MS;
                    this._interval = setTimeout(() => {
                        this._interval = null;
                        this.searchItems(cleanVal, false, {
                            // Receiving-style searchable fields: never auto-select a full location while typing.
                            autoSelect: this.isReceivingStyleLocationSearch()
                                ? false
                                : (this.useScannerField() ? false : !this.isManualTypingMode()),
                        });
                    }, searchDelay);
                    if (
                        !this.useScannerField() ||
                        this.isReceivingStyleLocationSearch()
                    ) {
                        onSelectItem(cleanVal);
                    }
                }}
            />
        );

        const cameraButton = showCameraButton ? (
            <TouchableOpacity
                onPress={() => this.openCameraScanner()}
                style={compactMobile ? styles.compactCameraBtn : styles.cameraBtn}
                accessibilityLabel="Scan location with camera"
            >
                <MaterialIcons name="photo-camera" color="#fff" size={compactMobile ? 20 : 24} />
            </TouchableOpacity>
        ) : null;

        return <View style={{
            ...styles.container,
            ...(compactMobile ? styles.containerCompact : null),
            ...(this.isReceivingStyleLocationSearch() ? { flex: 0, width: "100%", alignSelf: "stretch", minWidth: 0 } : null),
            paddingTop: width ? 0 : 5,
        }}>
            {showFromLocationModal ? (
                <InventoryTransferFromLocationModal
                    inventoryLocations={inventoryLocations || this.props.options || []}
                    fromLocation={fromLocationValue}
                    toLocation={toLocationValue}
                    initialItem={initialItem && typeof initialItem === "object" ? initialItem : (initialItem ? { item: initialItem, itemName: initialItem } : null)}
                    initialLotcodes={initialLotcodes}
                    readOnly={this.isInventoryTransferModalReadOnly()}
                    complete_date={complete_date}
                    onClose={() => this.setState({ showFromLocationModal: false })}
                    onSave={(rows) => this.onFromLocationModalSave(rows)}
                />
            ) : null}
            {transferFromLocationPill ? (
                <TouchableOpacity
                    onPress={() => this.openFromLocationModal()}
                    style={{
                        borderRadius: 20,
                        borderWidth: hasSelectedFromLocation ? 1.5 : 0,
                        borderColor: hasSelectedFromLocation ? colors.orange : colors.primary,
                        backgroundColor: hasSelectedFromLocation ? "#fff" : colors.primary,
                        paddingVertical: 8,
                        paddingHorizontal: 12,
                        minHeight: 36,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Text
                        numberOfLines={2}
                        style={{
                            color: hasSelectedFromLocation ? "#222" : "#fff",
                            fontSize: 14,
                            fontFamily: "Roboto",
                            textAlign: "center",
                        }}
                    >
                        {fromLocationPillLabel}
                    </Text>
                </TouchableOpacity>
            ) : transferToLocationReadOnly ? (
                <View
                    pointerEvents="none"
                    style={{
                        borderRadius: 20,
                        borderWidth: hasSelectedToLocation ? 1.5 : 0,
                        borderColor: hasSelectedToLocation ? colors.orange : colors.primary,
                        backgroundColor: hasSelectedToLocation ? "#fff" : colors.primary,
                        paddingVertical: 8,
                        paddingHorizontal: 12,
                        minHeight: 36,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Text
                        numberOfLines={2}
                        style={{
                            color: hasSelectedToLocation ? "#222" : "#fff",
                            fontSize: 14,
                            fontFamily: "Roboto",
                            textAlign: "center",
                        }}
                    >
                        {toLocationPillLabel}
                    </Text>
                </View>
            ) : compactMobile ? (
                <View style={styles.compactFieldShell}>
                    {scanInput}
                    {cameraButton}
                </View>
            ) : (
                <View style={showCameraButton ? [styles.inputRow, { minWidth: 0 }] : undefined}>
                    {scanInput}
                    {cameraButton}
                </View>
            )}
            {!transferFromLocationPill && !transferToLocationReadOnly && Platform.OS === "ios" && useScanner && (
                <InputAccessoryView nativeID={inputAccessoryViewID}>
                    <KeyboardBar
                        abcOptionPress={null}
                        scanOptionPress={() => this.showScanner(true)}
                    />
                </InputAccessoryView>
            )}
            {
                !(transferSearchPill && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) && items?.length > 0 && defaultLabel != "toLocation" && !showLocationsSearch &&
                <DropDownLocations
                    initialOption={this.state.locationSelected}
                    updated={this.state.locationSelected}
                    options={filtered}
                    defaultComponent={defaultComponent}
                    openList={openList}
                    allLabel={"All Locations"}
                    onSelect={(option) => {
                        onSelectItem(option)
                    }}
                    width={width}
                    isRecipe={isRecipe}
                    openDropDown={openDropDown}
                    backgroundColor={backgroundColor}
                    isLast={isLast}
                    isDropDownOpened={isDropDownOpened}
                    resetDropDown={resetDropDown}
                    showScanner={showScanner}
                />
            }
            {
                !(transferSearchPill && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) && filtered?.length > 0 && wasEdited && this.isReceivingStyleLocationSearch() &&
                <View style={styles.listContainer}>
                    {filtered.map((item, idx) => (
                        <TouchableOpacity
                            key={idx}
                            style={styles.row}
                            onPress={() => this.selectLocation(item)}
                        >
                            <View style={styles.rowText}>
                                <Text style={styles.rowLabel}>{this.renderLocation(item)}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.rowBtn}
                                onPress={() => this.selectLocation(item)}
                            >
                                <MaterialCommunityIcons name="plus" size={22} color={colors.selectSearch} />
                            </TouchableOpacity>
                        </TouchableOpacity>
                    ))}
                </View>
            }
            {
                !(transferSearchPill && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) && (value && filtered?.length <= 0 && wasEdited && !(showLocationsSearch && (isShipmentByLocation || isTransferByLocation) && useScanner && !this.isReceivingStyleLocationSearch())) ? <View>
                    <Text>No locations were found</Text>
                </View> : null
            }
            {scan && useScanner ? (
                <BarcodeScannerComponent
                    onBarCodeScanned={(data) => this.handleBarCodeScanned(data)}
                    closeScanner={() => {
                        this.showScanner(false);
                        const { setFormScroll } = this.props;
                        if (setFormScroll) setFormScroll(true);
                        if (scannerInput && scannerFieldForInput) {
                            this.setState({ scanerCanceled: true }, () => {
                                setTimeout(() => {
                                    if (!this._mounted) return;
                                    this.resumeWedgeHardware();
                                }, 500);
                            });
                        }
                    }}
                />
            ) : null}
        </View>
    }
}

export default connect((state, ownProps = {}) => ({
    isLoading: state.forms.getIn(["form", "status"]) === "loading",
    company_id: state.user.get("company_id"),
    submissionStatus: state.submissions.get("submissionSelected")?.status,
}), (dispatch, props) => ({
    searchItems: (formid, saveAsTemplate, saveAsForm, callback) =>
        dispatch(searchItems(formid, saveAsTemplate, saveAsForm, callback)),
    getMyCustomers: (data, callback) =>
        dispatch(getMyCustomers(data, callback)),
    getMySuppliers: (id, page, filter, callback) =>
        dispatch(getMySuppliers(id, page, filter, callback)),
}))(SearchInventoryLocations);

const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        paddingTop: 5,
        flex: 1,
    },
    containerCompact: {
        flex: 0,
        width: "100%",
        alignSelf: "stretch",
        paddingTop: 0,
    },
    compactFieldShell: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        minWidth: 0,
        minHeight: 42,
        borderWidth: 1,
        borderColor: "#e7e7e7",
        borderRadius: 5,
        backgroundColor: "#fff",
        overflow: "hidden",
    },
    compactInput: {
        flex: 1,
        minWidth: 0,
        borderWidth: 0,
        color: "#424242",
        fontFamily: "Roboto",
        fontSize: 16,
        fontWeight: "400",
        paddingHorizontal: 8,
        paddingVertical: 8,
        height: 42,
        backgroundColor: "transparent",
    },
    compactCameraBtn: {
        width: 40,
        height: 40,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.primary,
        flexShrink: 0,
        marginRight: 1,
        borderRadius: 4,
    },
    inputRow: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        minWidth: 0,
    },
    inputWithCamera: {
        flex: 1,
        minWidth: 0,
        marginRight: 8,
    },
    cameraBtn: {
        width: 44,
        height: 44,
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 22,
        backgroundColor: colors.primary,
        flexShrink: 0,
    },
    input: {
        borderColor: "#e7e7e7",
        borderRadius: 5,
        borderWidth: 1,
        color: "#424242",
        fontFamily: "Roboto",
        fontSize: 22,
        fontWeight: "400",
        paddingLeft: 10,
    },
    label: {
        fontSize: 20,
        color: '#939598',
        fontWeight: '300',
        fontFamily: 'Roboto',

    },
    listContainer: {
        paddingTop: 4,
        width: "100%",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomColor: "#e7e7e7",
    },
    rowText: {
        flex: 1,
        paddingRight: 8,
    },
    rowLabel: {
        fontFamily: "Roboto",
        fontSize: 18,
        color: "#424242",
    },
    rowBtn: {
        borderRadius: 4,
        padding: 6,
        borderWidth: 1,
        borderColor: colors.selectSearch,
        alignItems: "center",
        justifyContent: "center",
        minWidth: 36,
        minHeight: 36,
    },
});